import React, { useEffect, useCallback, useRef, useState } from "react";
import { AmiraResources, AssignmentManifest, parseMasteryPacingToClassName, RootState, Student, WeeklyPlanStateData} from "@/models";
import { PlannerCurriculumLesson } from "@/features/planner/PlannerTypes";
import { SkillType, MasteryStatus, MasteryPacing, MasteryTimeBooked, MasteryStatusPacing, ResourceType } from "@/enums";
import { getSkillTimeBalance, getTimeAssignedByGrade } from "@/utilities";
import { getMondayFridayDates } from "@/features/dashboard/utils";
import { useRosterInfo } from "@/hooks";

// Store
import { useDispatch, useSelector } from "react-redux";
import {
  initializeWeeklyPlanData,
  resetWeekState,
  selectResourcesLoading,
  selectWeeklyPlan,
  selectWeeklyPlanData,
  selectWeeklyPlanIsLoading,
  updateWeeklyPlanDates,
  WeeklyPlanState,
} from "@/store/slices";
import { isTeacherResourceNotForSkill } from "@/components/Global/AmiraMessage/ModalTemplates/LessonPlanAutoFixUtils";
import { getSkillResources } from "@/features/planner/utils/getSkillResources";

const TEMPORARY_PINELLAS_DISTRICT_ID = 99999999999999999000000803;

export const useWeeklyPlan = () => {
  const dispatch = useDispatch();
  const weeklyPlan = useSelector(selectWeeklyPlan) as WeeklyPlanState;
  const weeklyPlanData = useSelector(selectWeeklyPlanData) as WeeklyPlanStateData;
  const {dateFrom, dateTo} = weeklyPlanData;
  const { userId, classroomId, students, reports, userData } = useRosterInfo() as { userId: string, classroomId: string, students: Student[], reports: any, userData: any };
  const prevClassroomIdRef = useRef<string>();
  const { districtId } = userData;
  const [failedToLoad, setFailedToLoad] = useState(false);
  const isWeeklyPlanLoading = useSelector(selectWeeklyPlanIsLoading);
  const areResourcesLoading = useSelector(selectResourcesLoading);

  useEffect(() => {
    if (weeklyPlan.error) {
      setFailedToLoad(true);
    } else {
      setFailedToLoad(false);
    }
  }, [weeklyPlan.error]);

  // Adapt the starting date to the startDate of the academic calendar
  useEffect(() => {
    return; // Return early until we're ready to use this feature
    if (districtId && classroomId && districtId === TEMPORARY_PINELLAS_DISTRICT_ID ) {
      const calendarStartDate = new Date('2025-08-11'); // TODO: retrieve from academic calendar
      const [newDateFrom, newDateTo] = getMondayFridayDates( 0, new Date(calendarStartDate.setHours(24,0,0,0)));
      // Don't update if the dates are the same
      if (dateFrom !== newDateFrom && dateTo !== newDateTo) {
        dispatch(updateWeeklyPlanDates({ dateFrom: newDateFrom, dateTo: newDateTo }));
      }
    }
  }, [districtId, classroomId, dispatch, dateFrom, dateTo]);

  // initial page load of either the dashboard or lesson planner page
  useEffect(() => {
    if (!userId || !classroomId || !students || !students.length) return;
    
    const hasClassChanged = prevClassroomIdRef.current !== undefined && prevClassroomIdRef.current !== classroomId;
    if (hasClassChanged) {
      dispatch(resetWeekState());
    }
    
    prevClassroomIdRef.current = classroomId;
    
    // Use the new Redux-managed initialization instead of local fetch logic
    dispatch(initializeWeeklyPlanData() as any);
  }, [
    dispatch,
    userId,
    classroomId,
    students,
  ]);

  const reloadData = () => {
    dispatch(resetWeekState());
    dispatch(initializeWeeklyPlanData() as any);
  };

  const getTimeAssigned = useCallback(
    (lessonPlanId: string, studentId: string, skillId: string, masteryGroup: string): number => {
      const lesson = weeklyPlanData.curriculum?.tracks
        ?.flatMap(({ units }) => units ?? [])
        .flatMap(({ lessons }) => lessons ?? [])
        .find((l) => l.lessonPlanId === lessonPlanId);
      return lesson?.timeAssigned?.[skillId]?.[masteryGroup]?.[studentId] ?? 0;
    },
    [weeklyPlanData]
  );

  const subtractAssignmentTime = (availableTimeResources: { [skillId: string]: Map<string, number> }, manifest: AssignmentManifest[], studentId: string, studentGrades: { grade: string }[]) => {
    for (const m of manifest) {
      const skillId = m.skills?.[0]?.skillId;
      if (skillId) {
       const time = availableTimeResources[skillId]?.get(studentId);
       if (time) {
         availableTimeResources[skillId].set(studentId, time - getTimeAssignedByGrade(m.activityType ?? '', studentGrades));
       }
      }          
    }
  }

  const subtractTeacherResources = (availableTimeResources: { [skillId: string]: Map<string, number> }, resources: string[], studentId: string, studentGrades: { grade: string }[]) => {
    for (const resource of resources??[]) {
      for (const r of weeklyPlanData.resources) {
        if (r.resources.find(res=> res.resourceUniqueId === resource)) {
          const time = availableTimeResources[r.skillId]?.get(studentId);
          if (time) {
            availableTimeResources[r.skillId].set(studentId, time - getTimeAssignedByGrade(ResourceType.teacherResource, studentGrades));
          }
        }
      }
    }
  }

  const getLessonBookingStatus = useCallback(
    (lessonPlanId: string, students: Student[]): {records: Record<MasteryTimeBooked, Set<string>>, isEnoughResources: boolean} => {
      const alertStatus = [
        MasteryTimeBooked.OVER_BOOKED,
        MasteryTimeBooked.UNDER_BOOKED,
        // MasteryTimeBooked.NO_DATA
      ]
      const initialReturn = {} as Record<MasteryTimeBooked, Set<string>>;
      if (
        !weeklyPlanData.curriculum 
        || !weeklyPlanData.mastery
        || !students
        || students.length === 0
        || isWeeklyPlanLoading
        || areResourcesLoading
      ) return {records: initialReturn, isEnoughResources: true};
      const studentGrades = students.map(s => ({ grade: s.grade.toString() }));
      const lessonMasteryRecords = weeklyPlanData.mastery[lessonPlanId] as
          { studentId: string, records: { skillId: string, pacing: MasteryStatus }[] }[] | undefined;
      const lesson: PlannerCurriculumLesson | undefined = (weeklyPlanData.curriculum.tracks ?? [])
        .flatMap(({units}) => units.flatMap(({lessons}) => lessons))
        .find(({lessonPlanId: lessonId}) => lessonId === lessonPlanId);

      if (!lesson || !lessonMasteryRecords) return {records: initialReturn, isEnoughResources: true};

      const lessonResourcesTotalTime: {
        skillId: string;
        time: number;
      }[] = [];
      const availableTimeResources: {
        [skillId: string]: Map<string, number>;
      } = {};
      const lessonFocusSkills = lesson.skills.filter(s => s.skillType === SkillType.FOCUS);
      const lessonOnlySkillResources: {
        skillId: string;
        resources: AmiraResources[];
      }[] = [];
      for (const skill of lessonFocusSkills) {
        if (!skill.skillId) continue;
        lessonOnlySkillResources.push({
          skillId: skill.skillId,
          resources: getSkillResources({
            skillId: skill.skillId,
            skillResources: weeklyPlanData.resources,
            lessonName: lesson.lessonName,
            curriculumId: weeklyPlanData.curriculum.id,
            lessonSkills: lesson.skills.filter(s =>s.skillType === SkillType.FOCUS).map(s => ({skillId: s.skillId || "", displayName: s.displayName || ""})) ?? []
          }),
        });
      }
      // if more than one skill, we remove the teacher resources for lesson and curriculum level
      if (lessonOnlySkillResources.length > 1) {
        for (const resource of lessonOnlySkillResources) {
          resource.resources = resource.resources.filter(
            r =>
              r.resourceType !== ResourceType.teacherResource ||
              !isTeacherResourceNotForSkill(r, resource.skillId)
          );
        }
      }
     
      for (const resource of lessonOnlySkillResources) {
        lessonResourcesTotalTime.push({skillId: resource.skillId, time: resource.resources.reduce((acc, {resourceType}) => acc + getTimeAssignedByGrade(resourceType, studentGrades), 0)});
      }

      // initialize the available time resources for each skill and student
      for (const resource of lessonResourcesTotalTime) {
        if (!availableTimeResources[resource.skillId]) {
          availableTimeResources[resource.skillId] = new Map<string, number>();
        }
        for (const student of students) {
          if (!student.id) continue;
          if (!availableTimeResources[resource.skillId].has(student.id)) {
            availableTimeResources[resource.skillId].set(student.id, resource.time);
          }
        }
      }    
     
      for (const student of students) {
        if (!student.id) continue;
        let hasStudentAssignment = false;
        // subtract student assignments
        for (const studentAssignment of lesson.studentAssignments) {
          if (studentAssignment.entityId === student.id) {
            subtractAssignmentTime(
              availableTimeResources,
              studentAssignment.manifest,
              student.id,
              studentGrades
            );

            // subtract teacher resources
            // the assumption here is that a student can have Lesson, Curriculum or Skill teacher resources
            // so we need to find each matching resource and subtract the time
            subtractTeacherResources(
              availableTimeResources,
              studentAssignment.resources ?? [],
              student.id,
              studentGrades
            );
            hasStudentAssignment = true;
            continue;
          }
        }

        if (!hasStudentAssignment) {
          subtractAssignmentTime(
            availableTimeResources,
            lesson.mainAssignment?.manifest ?? [],
            student.id,
            studentGrades
          );
          subtractTeacherResources(
            availableTimeResources,
            lesson.mainAssignment?.resources ?? [],
            student.id,
            studentGrades
          );
        }
      } 

      const notEnoughResourcesForSkills: Set<string> = new Set();

      const result = students.reduce(
        (acc: Record<MasteryTimeBooked, Set<string>>, student) => {
          const studentMasteryRecord = lessonMasteryRecords.find(({studentId}) => studentId === student.id);
          for (const { skillId, timeToMaster } of lesson.skills.filter(({skillType}) => skillType === SkillType.FOCUS) ?? []) {
            if (!skillId || typeof timeToMaster !== 'number') continue;
            const pacingStandard = (studentMasteryRecord?.records.find(({skillId: sId}) => sId === skillId)?.pacing ?? MasteryPacing.NO_DATA) as MasteryPacing;
            const timeAssigned = lesson.timeAssigned?.[skillId ?? '']?.[parseMasteryPacingToClassName(pacingStandard)]?.[student.id] ?? 0;
            const masteryStatus: MasteryStatusPacing = pacingStandard === MasteryPacing.NO_DATA 
              ? MasteryStatusPacing.NO_DATA
                : pacingStandard === MasteryPacing.AHEAD_OF_GRADE_LEVEL
                  ? MasteryStatusPacing.LIKELY_MASTERED
                  : pacingStandard === MasteryPacing.AT_GRADE_LEVEL
                    ? MasteryStatusPacing.DEVELOPING
                      : pacingStandard === MasteryPacing.BELOW_GRADE_LEVEL
                        ? MasteryStatusPacing.NOT_DEVELOPED
                          : MasteryStatusPacing.DEVELOPING;
            const { skillBookingStatus, timeToGrowth } = getSkillTimeBalance( timeToMaster, timeAssigned, masteryStatus );
            let deltaTime = 0;
            if (!Number.isNaN(Number(timeToGrowth))) {
              deltaTime = Number(timeToGrowth) - timeAssigned;
            }
            // Add to the parent accumulator if we are tracking this status
            if (alertStatus.includes(skillBookingStatus)) {
              if (skillBookingStatus === MasteryTimeBooked.OVER_BOOKED) {
                // Skip overbooked students for now (WOM-822)
                // If we want to show overbooked students, we need to remove this check
                return acc;
              }
              if (!acc[skillBookingStatus]) {
                acc[skillBookingStatus] = new Set<string>();
              }
              if (skillBookingStatus === MasteryTimeBooked.UNDER_BOOKED) {
                if (availableTimeResources[skillId]?.get(student.id) && (availableTimeResources[skillId].get(student.id) ?? 0) < deltaTime) {
                  notEnoughResourcesForSkills.add(skillId);
                }
              }
              acc[skillBookingStatus].add(student.id);
            }
          }
          return acc;
        },
        {} as Record<MasteryTimeBooked, Set<string>>
      );
      if (notEnoughResourcesForSkills.size > 0) {
        console.log('☢️ Not enough resources for the skills: ', Array.from(notEnoughResourcesForSkills).join(' | '));
      }
      return {records: result, isEnoughResources: notEnoughResourcesForSkills.size === 0};
    },
    [weeklyPlanData, students, getTimeAssigned, isWeeklyPlanLoading, areResourcesLoading]
  );

  return {
    ...weeklyPlan,
    failedToLoad,
    getTimeAssigned,
    getLessonBookingStatus,
    reloadData,
  };
};
